import React from "react";

import { Grid } from "@mui/material";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import FieldView from "../../../../../../../../components/Form/FieldView";
import { ItemJob } from "../../../../../../../../models/sales";
import { StorageBox } from "../../../../../../../../models/storage.box";
import {
  Metric,
  convertBasicUnits,
  handleGetConvertValue,
} from "../../../../../../../../util/converts";
import { getMeasurement, getWeightMeasurement } from "../../../../../../../../util/getMeasurement";
import { getCurrentLocationStorageBox } from "../../../../../../../../util/getCurrentLocationStorageBox";

interface Props {
  storageBox: StorageBox;
  isFullDetails?: boolean;
}

export const StorageBoxDetail = ({ storageBox, isFullDetails }: Props) => {
  const { t: translate } = useTranslation();

  const getLocation = (): { location: string; map: string } => {
    return {
      location: getCurrentLocationStorageBox(storageBox, translate),
      map:
        storageBox?.mapCells && storageBox?.mapCells?.length > 0
          ? storageBox?.mapCells[0]?.map?.name || ""
          : "",
    };
  };

  const getLastMoved = (): string => {
    const { lastMovedAt } = storageBox;

    return lastMovedAt ? format(new Date(lastMovedAt), "dd/MM/yyyy p") : "";
  };

  const { code: measurementCode, decimalPlaces: measurementDecimalPlaces } = getMeasurement();
  const { code: weightMeasurementCode, decimalPlaces: weightMeasurementDecimalPlaces } =
    getWeightMeasurement();
  const getStorageBoxDimensions = (): {
    capacity: number;
    inUse: number;
    depth: number;
    height: number;
    weight: number;
  } => {
    const { convertMetersToMillimeters } = convertBasicUnits();
    const { itemsJobs: items, storageBoxType: type } = storageBox;
    const capacity = type?.capacity || 0;
    const { inUse, weight } = items?.reduce(
      (prev: { inUse: number; weight: number }, i: ItemJob) => {
        return {
          inUse: prev.inUse + (i.volume || 0),
          weight: prev.weight + (i.weight || 0),
        };
      },
      { inUse: 0, weight: 0 }
    ) || { inUse: 0, weight: 0 };
    const depth = convertMetersToMillimeters(type?.depth);
    const height = convertMetersToMillimeters(type?.height);

    return {
      capacity: Number(handleGetConvertValue(capacity, measurementCode, measurementDecimalPlaces)),
      inUse: Number(handleGetConvertValue(inUse, measurementCode, measurementDecimalPlaces)),
      depth,
      height,
      weight: Number(
        handleGetConvertValue(weight, weightMeasurementCode, weightMeasurementDecimalPlaces)
      ),
    };
  };

  const location = getLocation();
  const { capacity, inUse, depth, height, weight } = getStorageBoxDimensions();

  return (
    <>
      <Grid container direction="row" spacing={3}>
        <Grid item md={3}>
          <FieldView
            label={translate("ordersQuantity")}
            value={storageBox?.itemsJobs?.length}
            data-cy="field-view-orders-quantity"
          />
        </Grid>
        <Grid item md={3}>
          <FieldView
            label={translate("status")}
            value={storageBox?.storageStatus?.name || ""}
            data-cy="field-view-status"
          />
        </Grid>
        <Grid item md={3}>
          <FieldView
            label={translate("location")}
            value={location.map ? `${location.map} - ${location.location}` : location.location}
            data-cy="field-view-location"
          />
        </Grid>
        <Grid item md={3}>
          <FieldView
            label={translate("lastMoved")}
            value={getLastMoved()}
            data-cy="field-view-last-moved"
          />
        </Grid>
        <Grid item md={3}>
          <FieldView
            label={translate("capacity")}
            value={`${capacity} ${measurementCode}`}
            data-cy="field-view-capacity"
          />
        </Grid>
        <Grid item md={3}>
          <FieldView
            label={translate("inUse")}
            value={`${inUse} ${measurementCode}`}
            data-cy="field-view-in-use"
          />
        </Grid>
      </Grid>
      {isFullDetails && (
        <Grid container direction="row" spacing={3}>
          <Grid item md={4}>
            <FieldView
              label={translate("length")}
              value={`${depth} ${Metric.mm}`}
              data-cy="field-view-length"
            />
          </Grid>
          <Grid item md={4}>
            <FieldView
              label={translate("weight")}
              value={`${weight} ${weightMeasurementCode}`}
              data-cy="field-view-weight"
            />
          </Grid>
          <Grid item md={4}>
            <FieldView
              label={translate("height")}
              value={`${height} ${Metric.mm}`}
              data-cy="field-view-height"
            />
          </Grid>
        </Grid>
      )}
    </>
  );
};
